import { queryClient, queryKeys } from './query-client';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { docSource, docViewerKind } from './doc-viewer';
import { CONVERSION_CACHE_TIME_MS } from './doc-convert';

/** The Office kinds the pod renders to HTML — the `doc`/`slides`/`sheet` classifier kinds. */
export type ConvertibleThumbKind = 'doc' | 'slides' | 'sheet';

/**
 * Whether this file's thumbnail comes from a server-side conversion, and of which kind.
 *
 * A `.docx`/`.pptx` is a zip of xml: there is nothing in the stored content to read, and nothing a browser
 * can parse. The pod already converts both to markdown for the viewer (`/api/convert-document`), so the
 * thumbnail rides the same conversion rather than introducing a second way to read the same file.
 */
export function convertibleThumb(
  name: string | undefined,
  content: string | undefined,
  contentType?: string,
): { kind: ConvertibleThumbKind; url: string } | null {
  const kind = docViewerKind(name, content, contentType);
  if (kind !== 'doc' && kind !== 'slides' && kind !== 'sheet') return null;
  if (!content) return null;
  const source = docSource(content);
  // Only a remote source can be converted — the pod fetches the bytes itself.
  return source?.kind === 'remote' ? { kind, url: source.url } : null;
}

/** Whether this status is worth another attempt later. The route's own refusals are all 4xx — 400
 *  `host_not_allowed`, 413 `too_large`, 422 `unsupported_document` — and none of them can change for the
 *  same URL. Everything at 5xx is the pod or its upstream media fetch (502 `fetch_failed`, 504
 *  `fetch_timeout`, 500 `convert_failed`), which can. */
export function isTransientFailure(status: number): boolean {
  return status >= 500;
}

/**
 * The converted markdown for one Office file, fetched at most once per URL.
 *
 * Deliberately through the shared React Query cache rather than component state: a file can appear in a
 * gallery tile, a strip card and the viewer in the same session, and this way it converts once. The caller
 * decides WHEN to ask — the thumbnail only asks after scrolling into view, which is what keeps a conversion
 * per card off the forbidden per-row cost.
 */
export async function fetchConvertedDocument(url: string, kind: ConvertibleThumbKind): Promise<string | null> {
  return queryClient.fetchQuery({
    queryKey: queryKeys.convertedDocument(url),
    queryFn: async () => {
      const res = await apiFetch(`${API_BASE}/api/convert-document`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ url, kind }),
      });
      // A REFUSAL is an answer worth keeping; a FAILURE is not.
      //
      // Returning `null` for both would store a transient 502/504 as successful data — and under the long
      // cache below it would then be served as fresh for half an hour, so a tile that happened to be
      // scrolled into view during a blip stays blank until GC with no second attempt. Throwing instead
      // leaves the query in an error state, which react-query retries on the next mount (`retryOnMount`),
      // i.e. the next time the folder is opened.
      //
      // A 4xx is the route telling us about the DOCUMENT — unreadable, too large, a host we will not
      // fetch — and no retry changes any of those, so it is cached as the `null` it is.
      if (isTransientFailure(res.status)) throw new Error(`convert-document ${res.status}`);
      if (!res.ok) return null;
      const data = (await res.json()) as { html?: string };
      return data.html ?? null;
    },
    // A conversion is deterministic for a given URL, so it never needs refetching in a session.
    staleTime: Infinity,
    // ...but `staleTime` alone did not survive LEAVING the folder. A query with no observers is garbage
    // collected after `cacheTime` (5 min by default), so every tile re-converted on the way back in — the
    // exact round trip this cache exists to avoid. Shared with the viewer's `CONVERSION_CACHE`; see there
    // for why this is bounded rather than infinite.
    cacheTime: CONVERSION_CACHE_TIME_MS,
  });
}
